import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, CheckCircle2, Sparkles } from 'lucide-react';
import Button from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import AiGeneratedBadge from '@/components/ui/AiGeneratedBadge';
import { getOffer, IMPLIED_EQUIPMENT_PROFILE } from '../fixtures/sourceCatalog';
import { getDataType } from '../catalog';
import { DataTypeKey } from '../types';
import { IntegrationDemo } from '../demoStore';

/**
 * What enrichment derives, per data type — and by omission, what it does not.
 *
 * Only two of the six data types have anything to derive. Job roles and goals arrive from a system
 * of record as a title and a description; the clarity score, the required-skills list and the
 * equipment profile are Mowazaf's own fields, and a system of record has never had a reason to hold
 * them. Employees, departments, skills and assets arrive complete — there is no second Mowazaf-only
 * field waiting to be filled in — so offering enrichment on them ran a spinner, said "complete", and
 * derived nothing.
 *
 * The two lists differ because the underlying calculations differ: a goal has no required-skills
 * list to suggest. And within a list the items are not equivalent — the clarity score is the same
 * calculation that runs when a person saves the record by hand, while skills and equipment are a
 * guess at what a person would otherwise have typed into the form. That is why those two say so.
 */
const ENRICHMENT_DERIVATIONS: Partial<Record<DataTypeKey, string[]>> = {
  jobRoles: [
    'extInt.enrichment.derives.clarity',
    'extInt.enrichment.derives.skills',
    'extInt.enrichment.derives.equipment',
  ],
  goals: [
    'extInt.enrichment.derives.clarity',
    'extInt.enrichment.derives.equipment',
  ],
};

const derivesSomething = (dataType: DataTypeKey): boolean => dataType in ENRICHMENT_DERIVATIONS;

interface EnrichmentPanelProps {
  demo: IntegrationDemo;
  actorName: string;
}

const EnrichmentPanel: React.FC<EnrichmentPanelProps> = ({ demo, actorName }) => {
  const { t } = useTranslation();

  // Imported, not yet enriched, and of a type that has something to derive. The last clause is the
  // one that keeps this panel honest — without it an employees-only import was offered a button
  // whose entire effect was to report its own success.
  const enrichable = demo.state.points.filter(
    (p) => Boolean(p.lastUpdatedOn) && !p.enriched && derivesSomething(p.dataType),
  );
  const rolesPoint = demo.state.points.find((p) => p.dataType === 'jobRoles');
  const rolesSource = demo.state.sources.find((s) => s.id === rolesPoint?.sourceId);
  const rolesOffer = rolesSource ? getOffer(rolesSource.system, 'jobRoles') : undefined;
  const roleCount = rolesOffer ? rolesOffer.newCount + rolesOffer.updatedCount : 0;
  const rolesEnriched = Boolean(rolesPoint?.enriched);
  const assetsPoint = demo.state.points.find((p) => p.dataType === 'assets');
  // The advisory is the answer when equipment data exists nowhere, so it outlives enrichment.
  const showAdvisory = Boolean(rolesEnriched && assetsPoint?.owner === 'notTracked');
  // The panel must survive its own success. Dropping it the moment the last point is enriched made
  // the button vanish on click with nothing said, which read as the action having failed.
  const anyEnriched = demo.state.points.some((p) => p.enriched && derivesSomething(p.dataType));
  if (enrichable.length === 0 && !showAdvisory && !anyEnriched) return null;

  const busy = enrichable.some((p) => demo.busyKey === `enrich:${p.dataType}`);
  // Role-specific copy is only honest when job roles are part of what this run will enrich.
  const rolesInScope = enrichable.some((p) => p.dataType === 'jobRoles');
  const showRoleCopy = rolesInScope && roleCount > 0;
  // Role counts can only be quoted once job roles have actually been enriched; every other
  // finished run still needs to say that something happened.
  const bodyKey = rolesEnriched && roleCount > 0
    ? 'extInt.enrichment.doneBody'
    : anyEnriched
      ? 'extInt.enrichment.doneGeneric'
      : 'extInt.enrichment.body';

  // One at a time, because the store carries a single busy key.
  const handleRunEnrichment = async () => {
    for (const point of enrichable) {
      await demo.runEnrichment(point.dataType, actorName);
    }
  };

  return (
    <section className="rounded-lg border border-border bg-card p-5" data-testid="enrichment-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
            <h3 className="text-sm font-semibold text-foreground">{t('extInt.enrichment.heading')}</h3>
            <AiGeneratedBadge visible={anyEnriched} />
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            {t(bodyKey, { count: roleCount })}
          </p>

          {/* Named per data type rather than as one merged list, because the two lists are not the
              same and a customer who imported only goals should not be promised a skills list. */}
          {enrichable.length > 0 && (
            <dl className="mt-3 space-y-2.5" data-testid="enrichment-derives">
              {enrichable.map((p) => (
                <div key={p.dataType}>
                  <dt className="text-sm font-medium text-foreground">
                    {t(getDataType(p.dataType).labelKey)}
                  </dt>
                  <dd>
                    <ul className="mt-0.5 space-y-0.5 text-sm text-muted-foreground">
                      {ENRICHMENT_DERIVATIONS[p.dataType]!.map((key) => (
                        <li key={key}>{t(key)}</li>
                      ))}
                    </ul>
                  </dd>
                </div>
              ))}
            </dl>
          )}

          {/* The question every customer asks before pressing this. Answering it on the button's own
              panel is worth more than answering it in a meeting. */}
          {enrichable.length > 0 && (
            <p className="mt-3 text-sm text-muted-foreground" data-testid="enrichment-edits-kept">
              {t('extInt.enrichment.editsKept')}
            </p>
          )}
          {anyEnriched && (
            // A link, not a page name. Team Role Management lives in Core HR, which a company that
            // licensed engagement on its own does not show in the sidebar, so naming the screen sent
            // those customers looking for something they could not find. The route is role-gated
            // independently of navigation, and this panel only renders for Hr, so the link always
            // resolves for whoever can see it.
            <Link
              to="/core-hr/team-role-management"
              className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
              data-testid="enrichment-review-link"
            >
              {t('extInt.enrichment.reviewLink')}
              <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          )}

          {/* Without this the panel reads as a one-time onboarding step — press once, "complete",
              done forever. For a company whose HR system stays external it is the opposite: records
              keep arriving and descriptions keep changing, so enrichment has to keep running or the
              clarity score ends up describing a description that no longer exists. */}
          {anyEnriched && (
            <p className="mt-2 text-xs text-muted-foreground" data-testid="enrichment-ongoing">
              {t('extInt.enrichment.ongoing')}
            </p>
          )}
        </div>
        {enrichable.length > 0 ? (
          <Button onClick={() => { void handleRunEnrichment(); }} disabled={busy} data-testid="run-enrichment-btn">
            {busy && <Spinner size="sm" className="me-2" />}
            {showRoleCopy
              ? t('extInt.enrichment.run', { count: roleCount })
              : t('extInt.enrichment.runGeneric')}
          </Button>
        ) : (
          anyEnriched && (
            <span
              className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700 dark:text-green-400"
              data-testid="enrichment-complete"
            >
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              {t('extInt.enrichment.complete')}
            </span>
          )
        )}
      </div>

      {showAdvisory && (
        <div className="mt-4 rounded-md border border-border bg-muted/30 p-4" data-testid="equipment-advisory">
          <h4 className="text-sm font-medium text-foreground">{t('extInt.enrichment.advisoryHeading')}</h4>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('extInt.enrichment.advisoryBody', { roles: roleCount })}
          </p>
          <ul className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-foreground">
            {IMPLIED_EQUIPMENT_PROFILE.map((item) => (
              <li key={item.itemKey} className="tabular-nums">
                {item.quantity} {t(item.itemKey)}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-muted-foreground">{t('extInt.enrichment.advisoryFooter')}</p>
        </div>
      )}
    </section>
  );
};

export default EnrichmentPanel;
